import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { blocksApi, sectionsApi } from '../services/api';
import { DocumentImportBlock, DocumentImportResult, ImportFormat, Section } from '../types';
import DocumentPreview from '../components/DocumentPreview';
import {
  Upload,
  Download,
  BookOpen,
  FileText,
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  X,
  HelpCircle,
  Info,
  Eye,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const EXAMPLE_CSV = `name|abteil|inhalt|variablen|variablen_typen|variablen_optionen|variablen_hilfe
Mietgegenstand|Vertragsgegenstand|Der Vermieter vermietet an den Mieter die Wohnung in <<adresse>> bestehend aus <<anzahl_zimmer>> Zimmern.|adresse,anzahl_zimmer|TEXT,NUMBER||Vollständige Adresse eingeben,Anzahl der Zimmer
Mietdauer|Laufzeit und Kündigung|Das Mietverhältnis beginnt am <<mietbeginn>> und wird auf unbestimmte Zeit abgeschlossen.|mietbeginn|DATE||Datum des Mietbeginns
Mietzins|Vergütung|Der monatliche Mietzins beträgt <<mietzins>> EUR und ist bis zum <<faelligkeit>>. jeden Monats fällig.|mietzins,faelligkeit|CURRENCY,SELECT|;1./5./10./15.|Monatlicher Bruttomietzins,Fälligkeitstermin wählen
Kaution|Vergütung|Der Mieter leistet eine Kaution in Höhe von <<kaution>> EUR.|kaution|CURRENCY||Kautionsbetrag (max. 3 Monatsmieten)
Hausordnung|Rechte und Pflichten|Der Mieter verpflichtet sich zur Einhaltung der Hausordnung. Ruhezeiten sind von <<ruhezeit_von>> bis <<ruhezeit_bis>> Uhr einzuhalten.|ruhezeit_von,ruhezeit_bis|TEXT,TEXT||Beginn der Ruhezeit,Ende der Ruhezeit
Zahlungsart|Vergütung|Die Miete wird per <<zahlungsart>> beglichen.|zahlungsart|SELECT|Überweisung/Lastschrift/Bar|Bevorzugte Zahlungsart wählen`;

/** Trennzeile, die in der Datei zwei Bauteile voneinander trennt. */
const SEPARATOR = '*==============================*';

/** Unterstützte Dateiformate – deckungsgleich mit dem Server. */
const FORMATS: { id: ImportFormat; label: string; extensions: string[]; example: boolean }[] = [
  { id: 'rtf', label: 'RTF', extensions: ['.rtf'], example: true },
  { id: 'docx', label: 'Word', extensions: ['.docx', '.docm', '.dotx'], example: false },
  { id: 'html', label: 'HTML', extensions: ['.html', '.htm', '.xhtml'], example: true },
  { id: 'markdown', label: 'Markdown', extensions: ['.md', '.markdown', '.mdown', '.txt'], example: true }
];
const ACCEPTED_EXTENSIONS = FORMATS.flatMap(format => format.extensions);
const FORMAT_LABELS: Record<ImportFormat, string> = { rtf: 'RTF', docx: 'Word', html: 'HTML', markdown: 'Markdown' };

const downloadFile = (content: string, fileName: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Dateiinhalt unverändert als Base64 übertragen – so kommen auch Word-Dateien heil an. */
const toBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(index, index + chunkSize)));
  }
  return btoa(binary);
};

export default function BlocksImport() {
  const [tab, setTab] = useState<'document' | 'csv'>('document');

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/admin/blocks"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück zu Bauteile
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bauteile importieren</h1>
            <p className="mt-1 text-sm text-gray-500">
              Mehrere Bauteile auf einmal anlegen – formatiert aus RTF, Word, HTML oder Markdown oder als einfache CSV-Liste
            </p>
          </div>
          <Link
            to="/admin/blocks/import/anleitung"
            className="inline-flex items-center px-4 py-2 border border-primary-300 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 shrink-0"
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Ausführliche Anleitung
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-6" aria-label="Importart">
          {([
            { key: 'document', label: 'Dokument-Import (mit Formatierung)' },
            { key: 'csv', label: 'CSV-Import' }
          ] as const).map(entry => (
            <button
              key={entry.key}
              onClick={() => setTab(entry.key)}
              aria-current={tab === entry.key ? 'page' : undefined}
              className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium ${
                tab === entry.key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {entry.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'document' ? <DocumentImport /> : <CsvImport />}
    </div>
  );
}

// ==================== Dokument-Import ====================

function DocumentImport() {
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionId, setSectionId] = useState('');
  const [skipExisting, setSkipExisting] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileData, setFileData] = useState('');
  const [format, setFormat] = useState<ImportFormat | null>(null);
  const [preview, setPreview] = useState<DocumentImportBlock[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [results, setResults] = useState<DocumentImportResult[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    sectionsApi.getAll()
      .then(response => setSections(response.sections))
      .catch(() => setError('Abteile konnten nicht geladen werden'));
  }, []);

  const loadPreview = useCallback(async (data: string, name: string, targetSection: string) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await blocksApi.previewDocument(data, { fileName: name, sectionId: targetSection || null });
      setPreview(response.blocks);
      setFormat(response.format);
      setWarnings(response.warnings || []);
      setExpanded(new Set(response.blocks.length === 1 ? [response.blocks[0].index] : []));
    } catch (err: any) {
      setPreview(null);
      setError(err.response?.data?.error || 'Die Datei konnte nicht gelesen werden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const readFile = (file?: File | null) => {
    if (!file) return;
    if (!ACCEPTED_EXTENSIONS.some(extension => file.name.toLowerCase().endsWith(extension))) {
      setError(`Nicht unterstütztes Dateiformat. Möglich sind: ${ACCEPTED_EXTENSIONS.join(', ')}`);
      return;
    }
    const reader = new FileReader();
    reader.onload = event => {
      const data = toBase64(event.target?.result as ArrayBuffer);
      setFileName(file.name);
      setFileData(data);
      setResults(null);
      loadPreview(data, file.name, sectionId);
    };
    reader.onerror = () => setError('Die Datei konnte nicht gelesen werden');
    reader.readAsArrayBuffer(file);
  };

  const handleSectionChange = (value: string) => {
    setSectionId(value);
    if (fileData) loadPreview(fileData, fileName, value);
  };

  const handleImport = async () => {
    if (!fileData) return;
    setIsImporting(true);
    setError('');
    try {
      const response = await blocksApi.importDocument(fileData, { fileName, sectionId: sectionId || null, skipExisting });
      setResults(response.results);
      setWarnings(response.warnings || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Import fehlgeschlagen');
    } finally {
      setIsImporting(false);
    }
  };

  const handleDownloadExample = async (exampleFormat: ImportFormat) => {
    try {
      const content = await blocksApi.downloadDocumentExample(exampleFormat);
      const extension = FORMATS.find(entry => entry.id === exampleFormat)?.extensions[0] || '.txt';
      downloadFile(content, `bauteile-import-beispiel${extension}`, 'text/plain;charset=utf-8');
    } catch {
      setError('Beispieldatei konnte nicht geladen werden');
    }
  };

  const toggleExpanded = (index: number) => {
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const importable = (preview || []).filter(block => !block.error);
  const successCount = (results || []).filter(entry => entry.success).length;
  const skippedCount = (results || []).filter(entry => entry.skipped).length;
  const failedCount = (results || []).filter(entry => !entry.success && !entry.skipped).length;

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2 shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto" aria-label="Meldung schließen">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {results && (
        <div className={`mb-4 p-4 rounded-lg flex items-center ${
          failedCount === 0 ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
        }`}>
          <CheckCircle className="h-5 w-5 mr-2" />
          <span>
            <strong>{successCount}</strong> Bauteil(e) importiert
            {skippedCount > 0 && <>, <strong>{skippedCount}</strong> übersprungen</>}
            {failedCount > 0 && <>, <strong>{failedCount}</strong> fehlgeschlagen</>}
          </span>
          <Link to="/admin/blocks" className="ml-auto text-sm font-medium underline">Zu den Bauteilen</Link>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          <ul className="list-disc list-inside space-y-1">
            {warnings.map((warning, index) => <li key={index}>{warning}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Anleitung */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <HelpCircle className="h-5 w-5 mr-2 text-primary-500" />
            So funktioniert der Dokument-Import
          </h2>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Unterstützte Formate:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><strong>Word</strong> – <code className="bg-gray-100 px-1 rounded">.docx</code> direkt aus Microsoft Word</li>
                <li><strong>RTF</strong> – <code className="bg-gray-100 px-1 rounded">.rtf</code> aus Word, WordPad oder LibreOffice</li>
                <li><strong>HTML</strong> – <code className="bg-gray-100 px-1 rounded">.html</code>, auch Web-Exporte aus Word und Google Docs</li>
                <li><strong>Markdown</strong> – <code className="bg-gray-100 px-1 rounded">.md</code> für schnell erfasste Textbausteine</li>
              </ul>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 font-medium mb-2">Mehrere Bauteile pro Datei</p>
              <p className="text-sm text-blue-600">
                Setzen Sie zwischen zwei Bauteile eine eigene Zeile mit der Trennmarke:
              </p>
              <code className="mt-2 block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">{SEPARATOR}</code>
              <p className="text-xs text-blue-600 mt-2">
                Die Anzahl der Gleichheitszeichen ist beliebig (mindestens zwei), Leerzeichen werden ignoriert.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Optionale Kopfzeilen je Bauteil:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><code className="bg-gray-100 px-1 rounded">Name: Mietgegenstand</code> – Name des Bauteils</li>
                <li><code className="bg-gray-100 px-1 rounded">Abteil: Vertragsgegenstand</code> – Abteil (muss existieren)</li>
                <li><code className="bg-gray-100 px-1 rounded">Aktiv: nein</code> – Bauteil deaktiviert anlegen</li>
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                Ohne <code className="bg-gray-100 px-1 rounded">Name:</code> wird die erste Textzeile als Name verwendet,
                ohne <code className="bg-gray-100 px-1 rounded">Abteil:</code> das unten gewählte Ziel-Abteil.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Diese Formatierungen bleiben erhalten:</h3>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Fett, kursiv, unterstrichen, durchgestrichen, hoch-/tiefgestellt</li>
                <li>Schriftart, Schriftgröße, Text- und Hintergrundfarbe</li>
                <li>Ausrichtung, Einzüge, Absatzabstände, Zeilenabstand</li>
                <li>Überschriften, Aufzählungen und nummerierte Listen</li>
                <li>Tabellen, Seitenumbrüche, Links und eingebettete Bilder (PNG/JPEG)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Variablen im Text:</h3>
              <p className="text-sm text-gray-600">
                Platzhalter im Format <code className="bg-gray-100 px-1 rounded">&lt;&lt;variablenname&gt;&gt;</code> werden
                automatisch erkannt und als Variablen des Bauteils angelegt.
              </p>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-medium text-gray-700 mb-2">Beispieldatei herunterladen:</p>
              <div className="flex flex-wrap gap-2">
                {FORMATS.filter(entry => entry.example).map(entry => (
                  <button
                    key={entry.id}
                    onClick={() => handleDownloadExample(entry.id)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white rounded-lg hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {entry.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Für Word genügt eine beliebige <code className="bg-gray-100 px-1 rounded">.docx</code>-Datei mit Trennzeilen.
              </p>
              <Link to="/admin/blocks/import/anleitung" className="mt-3 inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700">
                <BookOpen className="h-4 w-4 mr-2" />
                Ausführliche Anleitung je Format öffnen
              </Link>
            </div>
          </div>
        </div>

        {/* Datei und Optionen */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Upload className="h-5 w-5 mr-2 text-primary-500" />
            Datei
          </h2>

          <label
            onDragOver={event => { event.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={event => { event.preventDefault(); setDragOver(false); readFile(event.dataTransfer.files?.[0]); }}
            className={`flex flex-col items-center justify-center px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-300 hover:bg-gray-50'
            }`}
          >
            <FileText className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-600">
              {fileName ? <strong className="text-gray-900">{fileName}</strong> : 'Datei auswählen oder hierher ziehen'}
            </span>
            <span className="text-xs text-gray-400 mt-1">
              {format ? `Erkanntes Format: ${FORMAT_LABELS[format]}` : 'Word, RTF, HTML oder Markdown (max. 20 MB)'}
            </span>
            <input
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(',')}
              className="hidden"
              onChange={event => readFile(event.target.files?.[0])}
            />
          </label>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="import-section">
              Ziel-Abteil
            </label>
            <select
              id="import-section"
              value={sectionId}
              onChange={event => handleSectionChange(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Ohne Abteil</option>
              {sections.map(section => (
                <option key={section.id} value={section.id}>{section.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Gilt für alle Bauteile ohne eigene <code className="bg-gray-100 px-1 rounded">Abteil:</code>-Kopfzeile.
            </p>
          </div>

          <label className="mt-4 flex items-center text-sm text-gray-700">
            <input
              type="checkbox"
              checked={skipExisting}
              onChange={event => setSkipExisting(event.target.checked)}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded mr-2"
            />
            Bauteile mit bereits vorhandenem Namen überspringen
          </label>

          <button
            onClick={handleImport}
            disabled={isImporting || isLoading || !importable.length}
            className="mt-6 w-full py-3 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isImporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Importiere...
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 mr-2" />
                {importable.length ? `${importable.length} Bauteil(e) importieren` : 'Bauteile importieren'}
              </>
            )}
          </button>

          {isLoading && (
            <p className="mt-3 text-sm text-gray-500 flex items-center">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mr-2" />
              Datei wird ausgewertet...
            </p>
          )}
        </div>
      </div>

      {/* Vorschau */}
      {preview && preview.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Eye className="h-5 w-5 mr-2 text-primary-500" />
            Gefundene Bauteile ({preview.length})
            {format && <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{FORMAT_LABELS[format]}</span>}
          </h2>

          <div className="space-y-3">
            {preview.map(block => {
              const result = results?.find(entry => entry.index === block.index);
              const open = expanded.has(block.index);
              return (
                <div
                  key={block.index}
                  className={`border rounded-lg ${block.error ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
                >
                  <button
                    onClick={() => toggleExpanded(block.index)}
                    aria-expanded={open}
                    className="w-full flex items-start gap-3 p-3 text-left"
                  >
                    {open ? <ChevronDown className="h-4 w-4 mt-1 text-gray-400 shrink-0" /> : <ChevronRight className="h-4 w-4 mt-1 text-gray-400 shrink-0" />}
                    <span className="flex-1 min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-mono text-gray-400">#{block.index}</span>
                        <span className="font-medium text-gray-900 truncate">{block.name}</span>
                        {block.sectionLabel && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{block.sectionLabel}</span>
                        )}
                        {!block.sectionLabel && !block.error && (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Ohne Abteil</span>
                        )}
                        {!block.isActive && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Inaktiv</span>
                        )}
                        {block.variables.length > 0 && (
                          <span className="text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">
                            {block.variables.length} Variable(n)
                          </span>
                        )}
                        {result && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            result.success ? 'bg-green-100 text-green-700' : result.skipped ? 'bg-gray-200 text-gray-600' : 'bg-red-100 text-red-700'
                          }`}>
                            {result.success ? 'Importiert' : result.skipped ? 'Übersprungen' : 'Fehlgeschlagen'}
                          </span>
                        )}
                      </span>
                      {block.error && <span className="block mt-1 text-sm text-red-700">{block.error}</span>}
                      {!block.error && result?.error && <span className="block mt-1 text-sm text-red-700">{result.error}</span>}
                      {block.warnings.map((warning, index) => (
                        <span key={index} className="block mt-1 text-sm text-yellow-700">{warning}</span>
                      ))}
                      {!open && (
                        <span className="block mt-1 text-sm text-gray-500 line-clamp-2">
                          {block.content.slice(0, 180)}{block.content.length > 180 ? '…' : ''}
                        </span>
                      )}
                    </span>
                  </button>

                  {open && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      {block.variables.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-1">
                          {block.variables.map(variable => (
                            <code key={variable} className="text-xs bg-white border border-gray-200 px-2 py-0.5 rounded">
                              &lt;&lt;{variable}&gt;&gt;
                            </code>
                          ))}
                        </div>
                      )}
                      <div className="bg-white border border-gray-200 rounded-lg p-6 overflow-x-auto">
                        <DocumentPreview document={block.document} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {preview && preview.length === 0 && !isLoading && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-600 flex items-start">
          <Info className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
          In der Datei wurde kein auswertbarer Inhalt gefunden.
        </div>
      )}
    </div>
  );
}

// ==================== CSV-Import ====================

function CsvImport() {
  const [csvContent, setCsvContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<any[] | null>(null);
  const [summary, setSummary] = useState<{ success: number; failed: number } | null>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCsvContent(content);
      setResults(null);
      setSummary(null);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleImport = async () => {
    if (!csvContent.trim()) {
      setError('Bitte CSV-Daten eingeben oder eine Datei hochladen');
      return;
    }

    setIsLoading(true);
    setError('');
    setResults(null);
    setSummary(null);

    try {
      const response = await blocksApi.importCsv(csvContent);
      setResults(response.results);
      setSummary({
        success: response.results.filter((r: any) => r.success).length,
        failed: response.results.filter((r: any) => !r.success).length
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Import fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadExample = () => {
    downloadFile(EXAMPLE_CSV, 'bauteile-import-beispiel.csv', 'text/csv;charset=utf-8;');
  };

  const loadExample = () => {
    setCsvContent(EXAMPLE_CSV);
    setResults(null);
    setSummary(null);
  };

  return (
    <div>
      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Success Summary */}
      {summary && (
        <div className={`mb-4 p-4 rounded-lg flex items-center ${
          summary.failed === 0
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
        }`}>
          <CheckCircle className="h-5 w-5 mr-2" />
          <span>
            <strong>{summary.success}</strong> Bauteile erfolgreich importiert
            {summary.failed > 0 && (
              <>, <strong>{summary.failed}</strong> fehlgeschlagen</>
            )}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CSV Format Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <HelpCircle className="h-5 w-5 mr-2 text-primary-500" />
            CSV-Format
          </h2>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 font-medium mb-2">
                Trennzeichen: <code className="bg-blue-100 px-2 py-0.5 rounded">|</code> (Pipe)
              </p>
              <p className="text-sm text-blue-600">
                Verwenden Sie das Pipe-Zeichen als Trennzeichen, da Kommas häufig im Inhalt vorkommen.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Pflichtfelder:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><code className="bg-gray-100 px-1 rounded">name</code> - Name des Bauteils</li>
                <li><code className="bg-gray-100 px-1 rounded">abteil</code> - Name des Abteils (muss existieren)</li>
                <li><code className="bg-gray-100 px-1 rounded">inhalt</code> - Inhalt/Text des Bauteils</li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Optionale Felder:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><code className="bg-gray-100 px-1 rounded">variablen</code> - Kommagetrennte Liste von Variablen</li>
                <li><code className="bg-gray-100 px-1 rounded">variablen_typen</code> - Typen der Variablen (kommagetrennt)</li>
                <li><code className="bg-gray-100 px-1 rounded">variablen_optionen</code> - Auswahloptionen für SELECT (;-getrennt pro Variable, /-getrennt für Optionen)</li>
                <li><code className="bg-gray-100 px-1 rounded">variablen_hilfe</code> - Hilfetexte/Tooltips (kommagetrennt)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Variablentypen:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><code className="bg-gray-100 px-1 rounded">TEXT</code> - Textfeld (Standard)</li>
                <li><code className="bg-gray-100 px-1 rounded">NUMBER</code> - Zahlenfeld</li>
                <li><code className="bg-gray-100 px-1 rounded">DATE</code> - Datumsauswahl</li>
                <li><code className="bg-gray-100 px-1 rounded">CURRENCY</code> - Währungsfeld (€)</li>
                <li><code className="bg-gray-100 px-1 rounded">BOOLEAN</code> - Ja/Nein Auswahl</li>
                <li><code className="bg-gray-100 px-1 rounded">SELECT</code> - Dropdown-Auswahl</li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Variablen im Inhalt:</h3>
              <p className="text-sm text-gray-600">
                Variablen werden mit <code className="bg-gray-100 px-1 rounded">&lt;&lt;variablenname&gt;&gt;</code> markiert
                und automatisch erkannt.
              </p>
            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <button
                onClick={handleDownloadExample}
                className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white rounded-lg hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Beispiel-CSV
              </button>
              <button
                onClick={loadExample}
                className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-primary-300 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100"
              >
                <FileText className="h-4 w-4 mr-2" />
                Beispiel laden
              </button>
            </div>
          </div>

          {/* Example Preview */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Beispiel-Vorschau:</h3>
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-xs text-green-400 whitespace-pre-wrap font-mono">
{EXAMPLE_CSV}
              </pre>
            </div>
          </div>
        </div>

        {/* CSV Input */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Upload className="h-5 w-5 mr-2 text-primary-500" />
            CSV-Daten
          </h2>

          {/* File Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Datei hochladen
            </label>
            <input
              type="file"
              accept=".csv,.txt"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
            />
          </div>

          {/* Text Area */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Oder CSV-Daten direkt eingeben
            </label>
            <textarea
              value={csvContent}
              onChange={(e) => {
                setCsvContent(e.target.value);
                setResults(null);
                setSummary(null);
              }}
              rows={15}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              placeholder="name|abteil|inhalt|variablen&#10;Beispiel-Bauteil|Allgemeine Bestimmungen|Der Text mit <<variable>>|variable"
            />
          </div>

          {/* Import Button */}
          <button
            onClick={handleImport}
            disabled={isLoading || !csvContent.trim()}
            className="w-full py-3 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Importiere...
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 mr-2" />
                Bauteile importieren
              </>
            )}
          </button>

          {/* Results */}
          {results && results.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Import-Ergebnisse:</h3>
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                {results.map((result, index) => (
                  <div
                    key={index}
                    className={`p-3 flex items-center text-sm ${
                      result.success ? 'bg-green-50' : 'bg-red-50'
                    }`}
                  >
                    {result.success ? (
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                    )}
                    <span className="font-medium mr-2">Zeile {result.line}:</span>
                    <span className="truncate">{result.name}</span>
                    {result.error && (
                      <span className="ml-2 text-red-600">- {result.error}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-700 mb-1">Hinweise:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Die Kopfzeile (erste Zeile) muss die Spaltennamen enthalten</li>
              <li>Abteile müssen vor dem Import bereits im System existieren</li>
              <li>Variablen im Inhalt werden automatisch erkannt (Format: &lt;&lt;name&gt;&gt;)</li>
              <li>UTF-8 Encoding wird unterstützt</li>
            </ul>
            <Link to="/admin/blocks/import/anleitung" className="mt-2 inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700">
              <BookOpen className="h-4 w-4 mr-2" />
              Ausführliche Anleitung zum CSV-Aufbau
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
